import { createSlice } from '@reduxjs/toolkit';
import * as service from './projectStateChangeService';
import ApprovalTypes from './ApprovalTypes';

export const initialState = {};

const projectDisclaimersSlice = createSlice({
  name: 'projectApprovals',
  initialState,
  reducers: {
    startFetchingProjectApprovals: (state, action) => ({
      ...state,
      [action.payload.projectId]: {
        isPending: true,
      },
    }),
    setProjectApprovals: (state, action) => ({
      ...state,
      [action.payload.projectId]: {
        approvals: action.payload.approvals,
        isPending: false,
      },
    }),
  },
});

export default projectDisclaimersSlice.reducer;

// Actions
const { startFetchingProjectApprovals, setProjectApprovals } =
  projectDisclaimersSlice.actions;

// Selectors
export const getProjectApprovals = (projectId) => (state) =>
  state.projectApprovals[projectId] &&
  state.projectApprovals[projectId].approvals;

export const getIsProjectApprovalsPending = (projectId) => (state) =>
  state.projectApprovals[projectId] &&
  state.projectApprovals[projectId].isPending;

export const getProjectApprovalsChart = (projectId) => (state) => {
  const approvals = getProjectApprovals(projectId)(state);

  const approvalsChart = {
    isRequestedToBePaused: false,
    pauseApprovalId: null,
    isRequestedToBeArchived: false,
    archiveApprovalId: null,
    isRequestedToBeReactivated: false,
    reactivateApprovalId: null,
    isRequestedToBeUnarchived: false,
    unarchiveApprovalId: null,
  };

  if (approvals) {
    approvals.forEach((approval) => {
      if (approval.approvalType === ApprovalTypes.Pause) {
        approvalsChart.isRequestedToBePaused = true;
        approvalsChart.pauseApprovalId = approval.requestId;
      }
      if (approval.approvalType === ApprovalTypes.Reactivate) {
        approvalsChart.isRequestedToBeReactivated = true;
        approvalsChart.reactivateApprovalId = approval.requestId;
      }
      if (approval.approvalType === ApprovalTypes.Archive) {
        approvalsChart.isRequestedToBeArchived = true;
        approvalsChart.archiveApprovalId = approval.requestId;
      }
      if (approval.approvalType === ApprovalTypes.Unarchive) {
        approvalsChart.isRequestedToBeUnarchived = true;
        approvalsChart.unarchiveApprovalId = approval.requestId;
      }
    });
  }

  return approvalsChart;
};

// Pause & Reactivation

export const pauseProject =
  ({ projectId, userId }) =>
  () =>
    service.callPauseEndpoint('enable', projectId, userId);

export const reactivateProject =
  ({ projectId, userId }) =>
  () =>
    service.callPauseEndpoint('disable', projectId, userId);

// Approvals
export const fetchProjectApprovals =
  ({ projectId, userId }) =>
  async (dispatch) => {
    try {
      dispatch(startFetchingProjectApprovals({ projectId }));
      const approvals = await service.getProjectApproval(projectId, userId);
      dispatch(setProjectApprovals({ projectId, approvals }));
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

// Pause Requests

export const requestPauseProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callPauseApprovalEndpoint('request', projectId, userId, approvalId);

export const approveRequestPauseProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callPauseApprovalEndpoint('approve', projectId, userId, approvalId);

export const declineRequestPauseProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callPauseApprovalEndpoint('reject', projectId, userId, approvalId);

export const cancelRequestPauseProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callPauseApprovalEndpoint('delete', projectId, userId, approvalId);

// Reactivate Requests

export const requestReactivateProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callReactivateApprovalEndpoint(
      'request',
      projectId,
      userId,
      approvalId
    );

export const approveRequestReactivateProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callReactivateApprovalEndpoint(
      'approve',
      projectId,
      userId,
      approvalId
    );

export const declineRequestReactivateProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callReactivateApprovalEndpoint(
      'reject',
      projectId,
      userId,
      approvalId
    );

export const cancelRequestReactivateProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callReactivateApprovalEndpoint(
      'delete',
      projectId,
      userId,
      approvalId
    );

// Archiving & Unarchiving

export const archiveProject =
  ({ projectId, userId }) =>
  () =>
    service.callArchiveEndpoint('enable', projectId, userId);

export const unarchiveProject =
  ({ projectId, userId }) =>
  () =>
    service.callArchiveEndpoint('disable', projectId, userId);
// Archive requests

export const requestArchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callArchiveApprovalEndpoint(
      'request',
      projectId,
      userId,
      approvalId
    );

export const approveRequestArchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callArchiveApprovalEndpoint(
      'approve',
      projectId,
      userId,
      approvalId
    );

export const declineRequestArchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callArchiveApprovalEndpoint(
      'reject',
      projectId,
      userId,
      approvalId
    );

export const cancelRequestArchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callArchiveApprovalEndpoint(
      'delete',
      projectId,
      userId,
      approvalId
    );

// Unarchive requests

export const requestUnarchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callUnarchiveApprovalEndpoint(
      'request',
      projectId,
      userId,
      approvalId
    );

export const approveRequestUnarchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callUnarchiveApprovalEndpoint(
      'approve',
      projectId,
      userId,
      approvalId
    );

export const declineRequestUnarchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callUnarchiveApprovalEndpoint(
      'reject',
      projectId,
      userId,
      approvalId
    );

export const cancelRequestUnarchiveProject =
  ({ approvalId, projectId, userId }) =>
  async () =>
    service.callUnarchiveApprovalEndpoint(
      'delete',
      projectId,
      userId,
      approvalId
    );
